import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../utils/userContext";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { loginRequest } from "../authConfig";
import styles from "./SelectTenant.module.css";

const SelectTenant = () => {
  const authRequest = { ...loginRequest };
  const { tenants, setActiveTenantId, activeTenantId } = useUser();
  const navigate = useNavigate();
  const [selectingId, setSelectingId] = useState<any>(null);

  const normalizedTenants = useMemo(() => {
    const list = Array.isArray(tenants) ? tenants : [];
    return list
      .map((t) => {
        const id = String(t?.tenantId ?? t?.TenantId ?? "");
        const name =
          t?.tenantName ??
          t?.TenantName ??
          t?.name ??
          t?.businessName ??
          t?.BusinessName ??
          "Tenant";

        return { id, name: String(name) };
      })
      .filter((x) => x.id);
  }, [tenants]);

  const onSelect = async (tenantId: any) => {
    const id = String(tenantId);
    setSelectingId(id);

    // set context + persist (your provider does localStorage)
    setActiveTenantId(id);

    // give React a tick so UI can paint the selection state
    setTimeout(() => {
      navigate("/timeentry", { replace: true });
    }, 0);
  };

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={authRequest}
    >
      <div className="main-articles company-articles">
          {/* Page shell */}
          <div className={styles["page-shell"]}>
            <div className={styles["content-wrapper"]}>
              {/* Card */}
              <div className={styles.card}>
                {/* Card header row */}
                <div className={styles["card-header"]}>
                  <h2 className={styles["card-header-title"]}>
                    Available Tenants
                  </h2>

                  {!!activeTenantId && (
                    <div className={styles["current-tenant-info"]}>
                      <span className={styles["current-label"]}>Current:</span>
                      <span
                        className={styles["current-tenant-id"]}
                        title={String(activeTenantId)}
                      >
                        {String(activeTenantId)}
                      </span>
                    </div>
                  )}
                </div>

                {/* List */}
                <div className={styles["tenant-list"]}>
                  {normalizedTenants.length === 0 ? (
                    <div className={styles["empty-state"]}>
                      <svg className={styles["empty-state__icon"]} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="6" y="12" width="36" height="24" rx="4" stroke="#d1d5db" strokeWidth="1.5" />
                        <path d="M18 24h12" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" />
                      </svg>
                      <div className={styles["empty-state__title"]}>No Tenants Found</div>
                      <div className={styles["empty-state__subtitle"]}>No tenants are associated with your account.</div>
                    </div>
                  ) : (
                    normalizedTenants.map((t) => {
                      const isActive =
                        !!activeTenantId && String(activeTenantId) === t.id;
                      const isLoading = selectingId === t.id;

                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => onSelect(t.id)}
                          disabled={!!selectingId}
                          className={styles["tenant-button"]}
                          style={{
                            border: isActive
                              ? "1px solid rgba(59,130,246,.45)"
                              : "1px solid rgba(0,0,0,.08)",
                            background: isActive
                              ? "rgba(59,130,246,.06)"
                              : "#fff",
                            cursor: selectingId ? "not-allowed" : "pointer",
                            boxShadow: isActive
                              ? "0 6px 18px rgba(59,130,246,.12)"
                              : "0 4px 14px rgba(0,0,0,.04)",
                            opacity: selectingId && !isLoading ? 0.65 : 1,
                          }}
                          onMouseDown={(e) =>
                            (e.currentTarget.style.transform = "scale(0.995)")
                          }
                          onMouseUp={(e) =>
                            (e.currentTarget.style.transform = "scale(1)")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.transform = "scale(1)")
                          }
                        >
                          {/* Left icon */}
                          <div
                            className={styles["tenant-icon"]}
                            style={{
                              background: isActive
                                ? "rgba(59,130,246,.14)"
                                : "rgba(0,0,0,.04)",
                            }}
                            aria-hidden="true"
                          >
                            🏢
                          </div>

                          {/* Main text */}
                          <div className={styles["tenant-main-text"]}>
                            <div className={styles["tenant-name-row"]}>
                              <div
                                className={styles["tenant-name"]}
                                title={t.name}
                              >
                                {t.name}
                              </div>

                              {isActive && (
                                <span className={styles["active-badge"]}>
                                  Active
                                </span>
                              )}
                            </div>

                            <div
                              className={styles["tenant-id"]}
                              title={t.id}
                            >
                              {t.id}
                            </div>
                          </div>

                          {/* Right side */}
                          <div className={styles["right-side"]}>
                            {isLoading ? (
                              <span className={styles["selecting-text"]}>
                                Selecting…
                              </span>
                            ) : (
                              <span className={styles["chevron-icon"]} aria-hidden="true">
                                {/* › */}
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Footer help */}
                <div className={styles.footer}>
                  Tip: You can switch tenants anytime from your profile menu.
                </div>
              </div>
            </div>
          </div>
      </div>
    </MsalAuthenticationTemplate>
  );
};

export default SelectTenant;
